import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, ChevronDown, Phone } from "lucide-react";
import { HOTEL, NAV } from "@/lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [travelersOpen, setTravelersOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setTravelersOpen(false);
  }, [location.pathname]);

  const dark = !scrolled && location.pathname === "/";
  const textColor = dark ? "text-bone" : "text-ink";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-bone/85 backdrop-blur-xl border-b border-stone/10" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 flex items-center justify-between gap-8 h-24">
        <div className="flex items-center gap-8 2xl:gap-12 min-w-0">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src={HOTEL.logo} alt="Regency Inn & Suites logo" data-testid="nav-logo-img" className="h-14 md:h-16 w-auto rounded-md bg-bone p-1.5 shadow-sm" />
        </Link>

        <nav className="hidden xl:flex items-center gap-5">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setTravelersOpen(true)}
                onMouseLeave={() => setTravelersOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 text-[12px] tracking-[0.12em] uppercase whitespace-nowrap ${textColor} hover:text-amber transition-colors`}
                  data-testid="nav-travelers"
                >
                  {item.label}
                  <ChevronDown size={13} />
                </button>
                <AnimatePresence>
                  {travelersOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                    >
                      <div className="bg-bone border border-stone/15 rounded-md shadow-[0_20px_50px_rgba(0,0,0,0.12)] py-2 w-56">
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-${c.to.slice(1)}`}
                            className="block px-5 py-2.5 text-sm text-ink hover:bg-sand hover:text-amber-deep transition-colors"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.slice(1)}`}
                className={`link-underline text-[12px] tracking-[0.12em] uppercase whitespace-nowrap ${textColor} hover:text-amber transition-colors`}
              >
                {item.label}
              </Link>
            ),
          )}
        </nav>
        </div>

        <div className="flex items-center gap-4">
          <a
            href={HOTEL.phoneHref}
            data-testid="nav-call"
            className={`hidden 2xl:flex items-center gap-2 text-sm whitespace-nowrap ${textColor} hover:text-amber transition-colors`}
          >
            <Phone size={15} /> {HOTEL.phone}
          </a>
          <a
            href={HOTEL.booking}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-now"
            className="hidden sm:inline-flex items-center whitespace-nowrap rounded-full bg-forest px-6 py-2.5 text-sm font-medium text-bone hover:bg-amber transition-colors duration-300"
          >
            Book Now
          </a>
          <button
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-toggle"
            className={`xl:hidden ${textColor}`}
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-forest-deep text-bone xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center h-20 px-6">
              <img src={HOTEL.logo} alt="Regency Inn & Suites" className="h-12 w-auto rounded-md bg-bone p-1" />
              <button onClick={() => setOpen(false)} data-testid="mobile-close" className="text-3xl leading-none">
                ×
              </button>
            </div>
            <div className="px-6 py-8 flex flex-col gap-1 overflow-y-auto max-h-[calc(100vh-5rem)]">
              {NAV.flatMap((item) =>
                item.children
                  ? [
                      <span key={item.label} className="mt-4 text-xs uppercase tracking-widest text-amber">
                        {item.label}
                      </span>,
                      ...item.children.map((c) => (
                        <Link key={c.to} to={c.to} className="py-3 font-serif text-3xl border-b border-bone/10">
                          {c.label}
                        </Link>
                      )),
                    ]
                  : [
                      <Link key={item.to} to={item.to} className="py-3 font-serif text-3xl border-b border-bone/10">
                        {item.label}
                      </Link>,
                    ],
              )}
              <a
                href={HOTEL.booking}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex justify-center rounded-full bg-amber px-6 py-4 text-bone font-medium"
                data-testid="mobile-book-now"
              >
                Book Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
